import React, { useState, useEffect, useCallback } from 'react';

import { ExamTypeSelector } from '../../../Components/PacsStudySyncModal/ExamTypeSelector';
import { InstanceSelector } from '../../../Components/PacsStudySyncModal/InstanceSelector';
import { VideoPreviewModal } from '../../../Components/PacsStudySyncModal/VideoPreviewModal';

interface InstanceData {
  id: number;
  sopInstanceUID: string;
  sopClassUID: string;
  seriesInstanceUID: string;
  instanceNumber: string;
  transferSyntax: string;
  pixelSpacing: string;
  numberOfFrames: number;
  receivedAt: string;
  synced: boolean;
  syncAttemptedAt: string | null;
  syncError: string | null;
  filePath?: string;
  signedUrl?: string;
  signedUrlExpiry?: string;
}

interface StudyData {
  study_instance_uid: string;
  study_date: string;
  study_mode: string;
  is_locked: boolean;
  category?: string;
  serie?: Array<{
    created_at: string;
    updated_at: string | null;
    serie_instance_uid: string;
    study_instance_uid: string;
    type: string;
  }>;
  pregnancy?: {
    id: number;
    patient_id: string;
    estimated_due_date: string;
    last_menstrual_period: string;
    gestational_age_weeks: number;
    gestational_age_days: number;
    pregnancy_type: string;
    dating_method: string;
    fetal_presentation: string;
    planned_delivery_mode: string;
    pregnancy_status: string;
    closed_at?: string | null;
    outcome_date?: string | null;
    created_at: string;
    updated_at: string | null;
  };
  patient?: {
    id: string;
    patient_name: string;
    patient_last_name: string;
    birth_date: string;
  };
  user?: {
    id: string;
    first_name: string;
    last_name: string;
  };
}

interface PacsStudyDetailsData {
  status: string;
  message: string;
  data: {
    study: StudyData | null;
    id: number;
    studyInstanceUID: string;
    patientId: string | null;
    patientName: string;
    patientBirthDate: string;
    studyDate: string;
    studyTime: string;
    studyDescription: string | null;
    modality: string;
    referringPhysician: string | null;
    accessionNumber: string | null;
    lastMenstrualPeriod: string | null;
    numberOfInstances: number;
    receivedAt: string;
    sourceAeTitle: string;
    aeType: string;
    fullySync: boolean;
    syncedInstanceCount: number;
    unsyncedInstanceCount: number;
    instances: InstanceData[];
  };
  datetime: string;
  status_code: number;
}

const calculateGestationalAge = (lmpDate?: string) => {
  if (!lmpDate) return null;
  const lmp = new Date(lmpDate);
  const today = new Date();
  const diffTime = Math.abs(today.getTime() - lmp.getTime());
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  const weeks = Math.floor(diffDays / 7);
  const days = diffDays % 7;
  return { weeks, days, totalDays: diffDays };
};

const getDefaultCategory = (lmpDate: string): 'T1' | 'T2' | 'T3' | null => {
  const ga = calculateGestationalAge(lmpDate);
  if (!ga) return null;
  if (ga.weeks < 14) return 'T1';
  if (ga.weeks < 28) return 'T2';
  return 'T3';
};


export const PacsExaminationStep = ({
  pacsStudyDetails,
  studyExists,
  suggestedLMP,
  pregnancyData,
  onComplete,
}: {
  pacsStudyDetails: PacsStudyDetailsData;
  studyExists: boolean;
  suggestedLMP?: string | null;
  pregnancyData?: any;
  onComplete: (data: any) => void;
}) => {
  // --- STATE AND LOGIC (Updated for blind order functionality) ---
  const existingCategory = pacsStudyDetails?.data?.study?.category as 'T1' | 'T2' | 'T3' | undefined;
  const existingType = pacsStudyDetails?.data?.study?.serie?.[0]?.type as 'OB' | 'Blind' | 'GYN' | 'General' | undefined;
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [currentPreviewIndex, setCurrentPreviewIndex] = useState(0);
  const [examType, setExamType] = useState<'OB' | 'Blind' | 'GYN' | 'General' | null>(existingType || 'OB');
  const [studyCategory, setStudyCategory] = useState<'T1' | 'T2' | 'T3' | null>(existingCategory || null);
  const [selectedInstances, setSelectedInstances] = useState<Set<number>>(new Set());
  const [blindOrder, setBlindOrder] = useState<number[]>([0, 0, 0, 0, 0, 0, 0]); // Initialize with 7 empty slots
  const [selectedSweepBox, setSelectedSweepBox] = useState<number | null>(null);
  const [useIndexMode, setUseIndexMode] = useState(false);

  const gestationalAge = calculateGestationalAge(suggestedLMP || pregnancyData?.last_menstrual_period);
  
  const toggleInstanceSelection = useCallback((instanceId: number) => {
    setSelectedInstances(prev => {
      const newSelected = new Set(prev);
      newSelected.has(instanceId) ? newSelected.delete(instanceId) : newSelected.add(instanceId);
      return newSelected;
    });
  }, []);

  const selectAllInstances = () => {
      const unsyncedIds = pacsStudyDetails.data.instances
        .filter(inst => !inst.synced)
        .map(i => i.id);
      setSelectedInstances(new Set(unsyncedIds));
  };
  
  const clearAllSelections = () => {
    setSelectedInstances(new Set());
    setBlindOrder([0, 0, 0, 0, 0, 0, 0]);
    setSelectedSweepBox(null);
  };

  const handleBlindOrderChange = useCallback((newOrder: number[]) => {
    setBlindOrder(newOrder);
  }, []);

  const handleSweepBoxSelect = useCallback((index: number | null) => {
    setSelectedSweepBox(index);
  }, []);

  const handleUseIndexModeChange = useCallback((useIndexMode: boolean) => {
    setUseIndexMode(useIndexMode);
  }, []);

  useEffect(() => {
    const isObStudy = examType === 'OB' || examType === 'Blind';
    const hasValidCategory = isObStudy ? !!studyCategory : true;
    
    const hasInstances = examType === 'Blind' 
        ? blindOrder.filter(id => id !== 0).length === 7 // Exactly 7 assigned instances for blind studies
        : selectedInstances.size > 0;

    if (examType && hasValidCategory && hasInstances) {
      const sop_instance_uids = examType === 'Blind'
        ? blindOrder.filter(id => id !== 0).map(instanceId => {
            const instance = pacsStudyDetails.data.instances.find(inst => inst.id === instanceId);
            return instance?.sopInstanceUID;
          }).filter(Boolean)
        : pacsStudyDetails.data.instances
            .filter(instance => selectedInstances.has(instance.id) && !instance.synced)
            .map(instance => instance.sopInstanceUID);

      onComplete({
        examType,
        studyCategory,
        sop_instance_uids,
      });
    } else {
      onComplete(null); // Step is invalid
    }
  }, [examType, studyCategory, selectedInstances, blindOrder, pacsStudyDetails.data.instances, onComplete]);

  const openPreviewModal = useCallback((startIndex: number = 0) => {
    setCurrentPreviewIndex(startIndex);
    setPreviewModalOpen(true);
  }, []);

  const closePreviewModal = useCallback(() => {
    setPreviewModalOpen(false);
  }, []);
  
  const navigatePreview = useCallback(
    (direction: 'prev' | 'next') => {
      const instances = pacsStudyDetails?.data?.instances || [];
      setCurrentPreviewIndex((prevIndex) => {
        let newIndex: number;

        if (direction === 'prev') {
          newIndex = prevIndex > 0 ? prevIndex - 1 : instances.length - 1;
        } else {
          newIndex = prevIndex < instances.length - 1 ? prevIndex + 1 : 0;
        }

        return newIndex;
      });
    },
    [pacsStudyDetails]
  );
  
  return (
    <div className="bg-card-color-200 dark:bg-gray-900 p-4 sm:p-6 md:p-8 h-full overflow-y-auto">
      <div className="w-full mx-auto space-y-8">
        
        {/* Fixed category banner (if applicable) */}
        {studyExists && existingCategory && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4">
            <h4 className="text-sm font-medium text-blue-900 dark:text-blue-400 mb-2">Study Category (Fixed)</h4>
            <div className="flex items-center">
              <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-400">
                {existingCategory}
              </span>
              <span className="ml-3 text-sm text-blue-700 dark:text-blue-400">Category is fixed for existing studies</span>
            </div>
          </div>
        )}
      
        {/* Component for selecting exam type and category */}
        <ExamTypeSelector
          examType={examType}
          studyCategory={studyCategory}
          gestationalAge={gestationalAge}
          onExamTypeChange={setExamType}
          onStudyCategoryChange={studyExists ? () => {} : setStudyCategory}
          getDefaultCategory={getDefaultCategory}
          suggestedLMP={suggestedLMP}
          pregnancyData={pregnancyData}
          studyExists={studyExists}
        />

        {/* Single InstanceSelector that handles both regular and blind studies */}
        {pacsStudyDetails?.data?.instances && (
          <InstanceSelector
            instances={pacsStudyDetails.data.instances}
            selectedInstances={selectedInstances}
            onToggleSelection={toggleInstanceSelection}
            onSelectAll={selectAllInstances}
            onClearAll={clearAllSelections}
            onPreviewInstance={openPreviewModal}
            onPreviewAll={() => openPreviewModal(0)}
            examType={examType}
            blindOrder={blindOrder}
            onBlindOrderChange={handleBlindOrderChange}
            selectedSweepBox={selectedSweepBox}
            onSweepBoxSelect={handleSweepBoxSelect}
            useIndexMode={useIndexMode}
            onUseIndexModeChange={handleUseIndexModeChange}
          />
        )}
      </div>
      <VideoPreviewModal
        isOpen={previewModalOpen}
        instances={pacsStudyDetails?.data?.instances || []}
        currentIndex={currentPreviewIndex}
        selectedInstances={selectedInstances}
        onClose={closePreviewModal}
        onNavigate={navigatePreview}
        onToggleSelection={toggleInstanceSelection}
        examType={examType}
        blindOrder={blindOrder}
        onBlindOrderChange={handleBlindOrderChange}
        selectedSweepBox={selectedSweepBox}
        onSelectSweepBox={handleSweepBoxSelect}
        useIndexMode={useIndexMode}
      />
    </div>
  );
};